import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Switch,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import { DataGridPremium, GridColDef, GridActionsCellItem } from '@mui/x-data-grid-premium';
import {
  useOrgUnitMembers,
  useCreateOrgUnitMember,
  useUpdateOrgUnitMember,
  useDeleteOrgUnitMember,
} from '@/hooks/core/useOrgUnitMember.custom';
import { useOrgRoleFunctions } from '@/hooks/core/useOrgRoleFunction';
import { useUserSearch } from '@/hooks/identity/useIdentity';
import { OrgUnitMember } from '@/types/core/OrgUnitMember';
import { UserLookup } from '@/types/identity/Identity';
import { GRID_ROW_HEIGHT } from '@/lib/gridStyles';
import { MembershipStateChip } from './MembershipStateChip';
import { formatValidityBound, isoToDateInput, validityBoundToIso } from './orgUnitMembership';
import { Alert } from '@mui/material';

interface OrgUnitMembersTabProps {
  orgUnitId: string;
}

const SOURCE_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'info'> = {
  MANUAL: 'default',
  ENTRA_ID: 'primary',
  ACTIVE_DIRECTORY: 'info',
  HR_SYSTEM: 'secondary',
  SCIM: 'info',
};

export function OrgUnitMembersTab({ orgUnitId }: OrgUnitMembersTabProps) {
  const { t } = useTranslation();
  const { data: members, isLoading } = useOrgUnitMembers(orgUnitId);
  const deleteMutation = useDeleteOrgUnitMember();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<OrgUnitMember | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const handleDelete = (member: OrgUnitMember) => {
    if (!window.confirm(t('orgUnit.removeMemberConfirm', { name: member.userName }))) return;
    setActionError(null);
    deleteMutation.mutate(
      { id: member.id, orgUnitId },
      {
        onError: () => setActionError(t('orgUnit.removeMemberError')),
      },
    );
  };

  const openCreate = () => { setEditingMember(null); setDialogOpen(true); };
  const openEdit = (member: OrgUnitMember) => { setEditingMember(member); setDialogOpen(true); };

  const columns: GridColDef<OrgUnitMember>[] = useMemo(
    () => [
      { field: 'userName', headerName: t('common.user'), width: 220 },
      {
        field: 'role',
        headerName: t('common.role'),
        width: 160,
        renderCell: (params) => params.value || '—',
      },
      {
        field: 'isPrimary',
        headerName: t('orgUnit.primary'),
        width: 90,
        renderCell: (params) =>
          params.value ? (
            <Chip label={t('common.yes')} size="small" color="primary" variant="outlined" />
          ) : null,
      },
      {
        // Effective state, not the raw isActive flag: an expired membership is flagged active and
        // grants nothing, which used to be invisible here.
        field: 'isActive',
        headerName: t('orgUnit.membershipStatus', { defaultValue: 'Status' }),
        width: 120,
        renderCell: (params) => <MembershipStateChip member={params.row} />,
      },
      {
        field: 'validFrom',
        headerName: t('orgUnit.validFrom', { defaultValue: 'Valid from' }),
        width: 110,
        renderCell: (params) => formatValidityBound(params.value),
      },
      {
        field: 'validTo',
        headerName: t('orgUnit.validTo', { defaultValue: 'Valid to' }),
        width: 110,
        renderCell: (params) => formatValidityBound(params.value),
      },
      {
        field: 'source',
        headerName: t('common.source'),
        width: 130,
        renderCell: (params) => (
          <Chip
            label={params.value}
            size="small"
            color={SOURCE_COLORS[params.value] || 'default'}
            variant="outlined"
          />
        ),
      },
      { field: '_spacer', headerName: '', flex: 1, sortable: false, filterable: false, disableColumnMenu: true, resizable: false },
    ],
    [t],
  );

  const actionColumn: GridColDef<OrgUnitMember> = useMemo(
    () => ({
      field: 'actions',
      type: 'actions',
      headerName: '',
      width: 80,
      getActions: (params) => [
        <GridActionsCellItem
          key="edit"
          icon={<Edit fontSize="small" />}
          label={t('common.edit')}
          onClick={() => openEdit(params.row)}
        />,
        <GridActionsCellItem
          key="delete"
          icon={<Delete fontSize="small" />}
          label={t('common.delete')}
          onClick={() => handleDelete(params.row)}
        />,
      ],
    }),
    [deleteMutation.isPending, t],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
      {actionError && (
        <Alert severity="error" onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button variant="outlined" size="small" startIcon={<Add />} onClick={openCreate}>
          {t('orgUnit.addMember')}
        </Button>
        <Typography variant="subtitle2" color="text.secondary">
          {t('orgUnit.memberCount', { count: members?.length ?? 0 })}
        </Typography>
      </Box>

      <DataGridPremium
        rows={members || []}
        columns={[actionColumn, ...columns]}
        loading={isLoading}
        density="compact"
        rowHeight={GRID_ROW_HEIGHT}
        autoHeight
        disableRowSelectionOnClick
        hideFooter={(members?.length ?? 0) <= 10}
        pageSizeOptions={[10, 25]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
      />

      <MemberDialog
        open={dialogOpen}
        orgUnitId={orgUnitId}
        member={editingMember}
        onClose={() => setDialogOpen(false)}
        onSaved={() => setDialogOpen(false)}
      />
    </Box>
  );
}

// --- Add / Edit Member Dialog ---

interface MemberDialogProps {
  open: boolean;
  orgUnitId: string;
  member: OrgUnitMember | null; // null = create
  onClose: () => void;
  onSaved: () => void;
}

function MemberDialog({ open, orgUnitId, member, onClose, onSaved }: MemberDialogProps) {
  const { t } = useTranslation();
  const isEdit = !!member;

  const createMutation = useCreateOrgUnitMember();
  const updateMutation = useUpdateOrgUnitMember();
  const { data: roleFunctions } = useOrgRoleFunctions();

  // Create-only: user search/selection
  const [searchInput, setSearchInput] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserLookup | null>(null);
  const { data: searchResults, isLoading: isSearching } = useUserSearch(searchInput, open && !isEdit);

  // Shared fields (seeded from the member when editing)
  const [role, setRole] = useState(member?.role ?? '');
  const [isPrimary, setIsPrimary] = useState(member?.isPrimary ?? false);
  const [isActive, setIsActive] = useState(member?.isActive ?? true);
  const [validFrom, setValidFrom] = useState(isoToDateInput(member?.validFrom));
  const [validTo, setValidTo] = useState(isoToDateInput(member?.validTo));
  const [dialogError, setDialogError] = useState<string | null>(null);

  // Re-seed local state whenever the dialog opens for a different member.
  const dialogKey = `${open}:${member?.id ?? 'new'}`;
  const [seededKey, setSeededKey] = useState('');
  if (open && seededKey !== dialogKey) {
    setSeededKey(dialogKey);
    setRole(member?.role ?? '');
    setIsPrimary(member?.isPrimary ?? false);
    setIsActive(member?.isActive ?? true);
    setValidFrom(isoToDateInput(member?.validFrom));
    setValidTo(isoToDateInput(member?.validTo));
    setSelectedUser(null);
    setSearchInput('');
    setDialogError(null);
  }

  const activeRoleFunctions = (roleFunctions ?? []).filter(r => r.isActive);
  const pending = createMutation.isPending || updateMutation.isPending;
  // Mirrors the server-side rule; the point is to say so before the round trip, not instead of it.
  const rangeInvalid = !!validFrom && !!validTo && validTo < validFrom;

  const handleSubmit = () => {
    setDialogError(null);
    if (isEdit && member) {
      updateMutation.mutate(
        {
          id: member.id,
          data: {
            orgUnitId: member.orgUnitId,
            userId: member.userId,
            userName: member.userName,
            role: role || undefined,
            isPrimary,
            isActive,
            // Sent explicitly: the DTO carries these, so omitting them on a PUT silently cleared the
            // membership's validity window on every unrelated edit.
            validFrom: validityBoundToIso(validFrom, 'from'),
            validTo: validityBoundToIso(validTo, 'to'),
            source: member.source,
          },
        },
        {
          onSuccess: () => onSaved(),
          onError: (err) => setDialogError(err.message || t('orgUnit.removeMemberError')),
        },
      );
      return;
    }

    if (!selectedUser) return;
    createMutation.mutate(
      {
        orgUnitId,
        userId: selectedUser.id,
        userName: selectedUser.userName,
        role: role || undefined,
        isPrimary,
        isActive,
        validFrom: validityBoundToIso(validFrom, 'from'),
        validTo: validityBoundToIso(validTo, 'to'),
        source: 'MANUAL',
      },
      {
        onSuccess: () => onSaved(),
        onError: (err) => setDialogError(err.message || t('orgUnit.removeMemberError')),
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? t('orgUnit.editMember', { defaultValue: 'Edit member' }) : t('orgUnit.addMember')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        {dialogError && (
          <Alert severity="error" onClose={() => setDialogError(null)}>
            {dialogError}
          </Alert>
        )}

        {isEdit ? (
          <TextField label={t('common.user')} value={member?.userName ?? ''} size="small" disabled fullWidth />
        ) : (
          <Autocomplete
            options={searchResults || []}
            getOptionLabel={(option) => `${option.userName} (${option.name || option.userName})`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedUser}
            onChange={(_, value) => setSelectedUser(value)}
            inputValue={searchInput}
            onInputChange={(_, value) => setSearchInput(value)}
            openOnFocus
            loading={isSearching}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('orgUnit.searchUsers')}
                size="small"
                placeholder={t('common.search', { defaultValue: 'Search…' })}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isSearching ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            noOptionsText={t('orgUnit.noUsersFound')}
          />
        )}

        <FormControl size="small" fullWidth>
          <InputLabel>{t('common.role')}</InputLabel>
          <Select
            value={role}
            label={t('common.role')}
            displayEmpty
            onChange={(e) => setRole(e.target.value as string)}
          >
            <MenuItem value=""><em>{t('common.none', { defaultValue: 'None' })}</em></MenuItem>
            {activeRoleFunctions.map((r) => (
              <MenuItem key={r.code} value={r.code}>{r.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Leaving both empty means "always valid", which is what most memberships want — an empty
            bound is no limit, not an unset field the server will reject. */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label={t('orgUnit.validFrom', { defaultValue: 'Valid from' })}
            type="date"
            size="small"
            fullWidth
            value={validFrom}
            onChange={(e) => setValidFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText={t('orgUnit.validityOptionalHint', { defaultValue: 'Optional — empty means no limit' })}
          />
          <TextField
            label={t('orgUnit.validTo', { defaultValue: 'Valid to' })}
            type="date"
            size="small"
            fullWidth
            value={validTo}
            onChange={(e) => setValidTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            error={rangeInvalid}
            helperText={rangeInvalid
              ? t('orgUnit.validityRangeError', { defaultValue: 'Valid to must not be before Valid from' })
              : t('orgUnit.validityInclusiveHint', { defaultValue: 'Inclusive — valid through this day' })}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
          <FormControlLabel
            control={<Checkbox checked={isPrimary} onChange={(e) => setIsPrimary(e.target.checked)} />}
            label={t('orgUnit.primary')}
          />
          {isEdit && (
            <FormControlLabel
              control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
              label={t('common.active')}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={(!isEdit && !selectedUser) || pending || rangeInvalid}
        >
          {pending ? t('common.saving', { defaultValue: 'Saving...' }) : t('common.save', { defaultValue: 'Save' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
